import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import logger from '@eneco/common/log';
import { stripAccolades } from '@eneco/common/string';
import { useFormsPublicV2PostFormV2 } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { NotificationBox } from '@eneco/sparky';
import type { SubmitHandler } from 'react-hook-form';

import { GenericFormShell } from './generic-form/GenericFormShell';
import { prepareRequest } from './generic-form/request';
import type { FormValues } from './generic-form/types';
import type { GenericFormContent } from './generic-form-content.types';
import { GENERIC_FORM_SLOTS } from './slots';

/**
 * `GenericForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Unlike every other block in this registry it is not presentational: it submits to the Digital
 * Core, which is why `lib:cms-components` may now depend on `lib:dc`/`lib:hooks` and why
 * `CmsRouteContainer` mounts a `DigitalCoreProvider`.
 *
 * Validation is assembled from the rendered children (`buildFormSchema`), so a field type this lane
 * has not migrated yet leaves the rest of the form working rather than blocking submission.
 */
const GenericForm: FC = () => {
  const content = useContent<GenericFormContent>();
  const { label, businessUnit } = useDC();
  const { error, isError, isSuccess, send } = useFormsPublicV2PostFormV2();

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isError) logger.error('Wxd7AY', 'Failed to submit form', { error });
  }, [isError, error]);

  const submitForm: SubmitHandler<FormValues> = async values => {
    setIsSubmitting(true);
    try {
      await send({
        businessUnit,
        formId: stripAccolades(content.senderIdOption ?? ''),
        label,
        requestBody: prepareRequest(content.senderIdOption ?? '', values),
        recaptchaEnterpriseToken: values.recaptchaToken,
      } as never);
    } catch (submitError) {
      logger.error('Wxd7AY', 'Failed to submit form', { error: submitError });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GenericFormShell
      slots={GENERIC_FORM_SLOTS.main}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isSuccess={isSuccess}
      // The fallback text and the notification predate the shared shell; it renders the same as before.
      buttonText={linkWithUrl(content.formSubmitRedirectLink)?.text ?? 'Verstuur'}
      notification={isError && <NotificationBox isAlert title="Er ging iets mis" text="Probeer het later opnieuw." />}
    />
  );
};

export default GenericForm;
